'use client'

import { tw } from 'utils/classnames'
import { Link } from '@repo/web-ui/components/link'
import { useLocale } from 'next-intl'
import { addOrReplaceLocaleInUrl } from 'cf-i18n/helpers'

const CF_URL = process.env.NEXT_PUBLIC_CF_URL

export interface PaginationProps {
  count: number
  hideNextButton?: boolean
  hidePrevButton?: boolean
  onChange?: (event: React.ChangeEvent<unknown>, page: number) => void
  page: number
  urls?: string[]
  className?: string
  testid?: string
}

const defaultPageCss = 'flex h-[30px] w-[30px] cursor-pointer items-center justify-center rounded'

const decidePagesToRender = (page: number, count: number): (number | 'pre' | 'post')[] => {
  if (count === 0) {
    return [1]
  }

  if (count < 9) {
    return Array(count)
      .fill(true)
      .map((item, index) => index + 1)
  }

  if (page < 8) {
    return Array(8)
      .fill(true)
      .map((item, index) => index + 1)
  }

  if (page === count) {
    return [1, 2, 3, 'pre', page - 2, page - 1, page]
  }

  if (page + 1 === count) {
    return [1, 2, 3, 'pre', page - 2, page - 1, page, page + 1]
  }

  if (page + 2 === count) {
    return [1, 2, 3, 'pre', page - 2, page - 1, page, page + 1, page + 2]
  }

  if (page > 7) {
    return [1, 2, 3, 'pre', page - 2, page - 1, page, page + 1, page + 2, 'post']
  }

  return []
}

export function Pagination(props: PaginationProps) {
  const {
    page,
    count,
    onChange,
    hideNextButton = false,
    hidePrevButton = false,
    urls,
    className,
    testid,
  } = props

  const locale = useLocale()

  const handleChange = (e: React.MouseEvent<HTMLLIElement, MouseEvent>, value: number) => {
    if (onChange) {
      onChange(e, value)
    }
  }

  const pagesToRender = decidePagesToRender(page, count)

  const prevUrl = urls && page > 1 && !hidePrevButton ? urls[page - 2] : null
  const nextUrl = urls && page < count && !hideNextButton ? urls[page] : null

  return (
    <>
      {prevUrl ? (
        <link rel="prev" href={`${CF_URL}${addOrReplaceLocaleInUrl(prevUrl, locale)}`} />
      ) : null}
      {nextUrl ? (
        <link rel="next" href={`${CF_URL}${addOrReplaceLocaleInUrl(nextUrl, locale)}`} />
      ) : null}
      <nav data-testid={testid}>
        <ul className={tw('text-12 flex flex-row gap-[5px]', className)}>
          {page > 1 && !hidePrevButton ? (
            <li
              className={tw(defaultPageCss, 'bg-[#f6f7fb]')}
              onClick={e => {
                if (handleChange) {
                  handleChange(e, page - 1)
                }
              }}
            >
              {urls ? (
                <Link
                  className="flex h-full w-full items-center justify-center"
                  href={urls[page - 2]}
                >
                  {'←'}
                </Link>
              ) : (
                '←'
              )}
            </li>
          ) : null}
          {pagesToRender.map(item => {
            return (
              <li
                key={`pagination_${item}`}
                className={tw(
                  (item !== 'pre' && item !== 'post' && page === Number(item)) || count === 0
                    ? 'text-service-white bg-[#2c2d33] font-bold'
                    : 'bg-[#f6f7fb]',
                  defaultPageCss,
                )}
                onClick={e => {
                  if (handleChange) {
                    // oxlint-disable-next-line no-nested-ternary
                    handleChange(e, item === 'pre' ? 4 : item === 'post' ? page + 1 : Number(item))
                  }
                }}
              >
                {/* oxlint-disable-next-line no-nested-ternary */}
                {urls ? (
                  <Link
                    className="flex h-full w-full items-center justify-center"
                    // oxlint-disable-next-line no-nested-ternary
                    href={item === 'pre' ? urls[3] : item === 'post' ? urls[page] : urls[item - 1]}
                  >
                    {item !== 'pre' && item !== 'post' ? item : '...'}
                  </Link>
                ) : item !== 'pre' && item !== 'post' ? (
                  item
                ) : (
                  '...'
                )}
              </li>
            )
          })}
          {page !== count && !hideNextButton ? (
            <li
              className={tw(defaultPageCss, 'bg-[#f6f7fb]')}
              onClick={e => {
                if (handleChange) {
                  handleChange(e, page + 1)
                }
              }}
            >
              {urls ? (
                <Link className="flex h-full w-full items-center justify-center" href={urls[page]}>
                  {'→'}
                </Link>
              ) : (
                '→'
              )}
            </li>
          ) : null}
        </ul>
      </nav>
    </>
  )
}
